// Tarifa BASE de cobro a comercios, a nivel operador logístico.
//
// Es el nivel base de la cascada de precios: lo que se configura acá aplica a TODOS los
// comercios, y el contrato de cada uno puede sobreescribir lo que quiera (su tarifa
// propia gana) y hereda el resto. Antes de esto, el precio de cada comercio solo se podía
// cargar contrato por contrato desde /admin/contratos.
//
// A diferencia de la config de pago a repartidores y la de COD, esta NO es versionada: un
// documento por operador que se pisa al editar. Lo que sí se conserva es la auditoría de
// lo ya cobrado — cada línea de una nota guarda el precio con el que se emitió y de qué
// nivel salió.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Coins, Info, Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import {
  getCommercePricingConfig,
  saveCommercePricingConfig,
} from "../../../../api/billing";
import { showToast } from "../../../../utils/showToast";
import { apiErrorMessage, apiErrorStatus } from "../../../../utils/apiErrorMessage";
import { SectionCard } from "../../../../components/ui/SectionCard";
import { Button } from "../../../../components/ui/Button";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { Skeleton } from "../../../../components/ui/Skeleton";
import PostalCodeChipsInput, {
  validPostalCodes,
} from "../../../../components/postalCodeChipsInput";
import CommuneRatesEditor, {
  communeRatesFromApi,
  communeRatesToApi,
} from "../../billing/components/CommuneRatesEditor";
import VolumeTiersEditor, {
  volumeTiersFromApi,
  volumeTiersToApi,
} from "../../billing/components/VolumeTiersEditor";
import useAuth from "../../../../hooks/useAuth";
import { useCountry } from "../../../../context/CountryContext";
import { resolveOperatorCurrency } from "../../cobro/utils/cobroHelpers";

const inputCls =
  "h-9 rounded-md border border-border bg-bg px-2 text-sm w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";

// Catálogo CERRADO de categorías de sobredimensionado. Es el enum de
// `overSizedEvidence.category` en microservice-packages: un valor fuera de esta lista
// nunca va a matchear ningún paquete, así que se ofrece como select y no como texto libre.
const SIZE_CATEGORIES = ["XL", "2XL", "3XL"];

// Escalas de tarifa por volumen. Mismo catálogo cerrado que el enum del backend.
const VOLUME_SCALES = ["flat", "weekly", "monthly"];

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// El backend devuelve { data: … }. Se tolera el documento pelado porque el resto de
// /billing responde sin envoltorio y el proxy del BFF no lo normaliza.
const unwrap = (res) => {
  const body = res?.data;
  if (body && typeof body === "object" && !Array.isArray(body) && "data" in body) {
    return body.data;
  }
  return body ?? null;
};

// Las filas de tarifa por CP llevan un id propio solo para la `key` de React: los códigos
// pueden repetirse mientras el operador escribe y el índice del array no sirve como clave
// estable al quitar filas del medio.
let _postalUidSeq = 0;
const postalUid = () => `pc${++_postalUidSeq}`;

const formFromConfig = (cfg) => ({
  defaultBaseCost:
    cfg?.defaultBaseCost != null ? String(cfg.defaultBaseCost) : "",
  taxMode: cfg?.taxMode === "inclusive" || cfg?.taxMode === "exclusive" ? cfg.taxMode : "",
  zones: communeRatesFromApi(cfg?.zones),
  postalCodePricing: (Array.isArray(cfg?.postalCodePricing)
    ? cfg.postalCodePricing
    : []
  ).map((p) => ({
    id: postalUid(),
    codes: Array.isArray(p?.postalCodes) ? p.postalCodes : [],
    baseCost: p?.baseCost != null ? String(p.baseCost) : "",
  })),
  sizeSurcharges: (Array.isArray(cfg?.sizeSurcharges) ? cfg.sizeSurcharges : []).map(
    (s) => ({
      category: s?.category ?? "",
      multiplier: s?.multiplier != null ? String(s.multiplier) : "1",
      additionalCost: s?.additionalCost != null ? String(s.additionalCost) : "0",
    }),
  ),
  weeklyTiers: volumeTiersFromApi(cfg?.weeklyTiers),
  monthlyTiers: volumeTiersFromApi(cfg?.monthlyTiers),
  defaultVolumeScale: VOLUME_SCALES.includes(cfg?.defaultVolumeScale)
    ? cfg.defaultVolumeScale
    : "flat",
});

const EMPTY_FORM = formFromConfig(null);

/**
 * Formulario -> body del PUT. Es la ÚNICA fuente del payload: la usa tanto el guardado
 * como el cálculo de `dirty`, así que el botón Guardar se habilita exactamente cuando el
 * body cambiaría. Comparar el formulario crudo marcaría cambios donde no los hay (las
 * filas llevan ids de React que se regeneran en cada carga) y dejaría el botón siempre
 * activo, invitando a guardar de más en una config cuya versión aborta las generaciones
 * de nota en curso.
 *
 * `postalEnabled` decide si `postalCodePricing` viaja: en un país sin CP tarifable la
 * clave se OMITE por completo, no viaja como `[]`.
 */
const payloadFromForm = (form, postalEnabled) => {
  const payload = {
    // "" (el operador borró el valor) viaja como null EXPLÍCITO: ausente significaría
    // "no lo tocó" y dejaría la tarifa anterior en pie.
    defaultBaseCost:
      form.defaultBaseCost === "" ? null : Math.round(num(form.defaultBaseCost)),
    taxMode: form.taxMode === "" ? null : form.taxMode,
    zones: communeRatesToApi(form.zones),
    sizeSurcharges: form.sizeSurcharges
      .filter((s) => s.category.trim())
      .map((s) => ({
        category: s.category.trim(),
        multiplier: num(s.multiplier) || 1,
        additionalCost: Math.round(num(s.additionalCost)),
      })),
    // Las escalas por volumen viajan SIEMPRE (aunque vacías): el backend las trata como
    // `defaultBaseCost` —solo escribe lo que recibe— así que un `[]` explícito es la única
    // forma de borrar una escalera ya cargada. Y tienen que estar acá sí o sí: `dirty`
    // compara payloads, así que un campo que no entre en esta función nunca habilita el
    // botón Guardar por más que el formulario cambie.
    weeklyTiers: volumeTiersToApi(form.weeklyTiers),
    monthlyTiers: volumeTiersToApi(form.monthlyTiers),
    defaultVolumeScale: form.defaultVolumeScale,
  };

  if (postalEnabled) {
    payload.postalCodePricing = form.postalCodePricing
      .map((p) => ({
        // `validPostalCodes` deja cada código en su clave canónica y descarta los que no
        // son tarifables. El editor ya los marca en rojo en pantalla, así que el operador
        // ve el problema al escribirlo en vez de recibir un 400 al guardar.
        postalCodes: validPostalCodes(p.codes),
        baseCost: Math.round(num(p.baseCost)),
      }))
      .filter((p) => p.postalCodes.length > 0);
  }

  return payload;
};

// `company` y `loadingCompany` los pasa AdminSettings a todas sus secciones. Acá se usan
// para una sola cosa: la cobertura (`coverageAreas`) con la que se acotan los selectores
// de comuna. `loadingCompany` distingue "todavía no llegó" de "llegó y no hay cobertura"
// — ver el contrato de los 3 estados en useCommuneCatalog.
export default function CommercePricingSection({ company, loadingCompany = false }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const currency = resolveOperatorCurrency(user?.logisticCompany);
  // Las tarifas por código postal solo se ofrecen donde el CP identifica una zona
  // tarifable (hoy Argentina). El CP chileno de 7 dígitos no matchea ningún formato, así
  // que mostrar el editor ahí invitaría a cargar reglas que el motor nunca aplicaría.
  // Mismo gate que usa el editor de pago a repartidores.
  const { postalCodeRates } = useCountry();
  const postalEnabled = postalCodeRates?.enabled === true;

  const [form, setForm] = useState(EMPTY_FORM);
  const [saved, setSaved] = useState(EMPTY_FORM);
  const [configVersion, setConfigVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Carga fallida: NO se renderiza el formulario. Con los valores por defecto, un
  // Guardar mandaría un PUT que REEMPLAZA el documento de tarifas del operador
  // (esta config no está versionada) y le borraría la tarifa real. Se falla cerrado.
  const [loadError, setLoadError] = useState(null);
  // 403 del BFF: no es un fallo, es falta de permisos. Se explica aparte y sin
  // botón de reintentar, que no arreglaría nada.
  const [forbidden, setForbidden] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    setForbidden(null);
    try {
      const cfg = unwrap(await getCommercePricingConfig());
      const next = formFromConfig(cfg);
      setForm(next);
      setSaved(next);
      setConfigVersion(cfg?.configVersion ?? 0);
    } catch (err) {
      if (apiErrorStatus(err) === 403) {
        // El BFF responde {message:"Forbidden"} sin detalle: apiErrorMessage()
        // descarta ese texto genérico y usa el fallback explicativo.
        setForbidden(
          apiErrorMessage(
            err,
            t("Tu usuario no tiene permisos para ver la tarifa de cobro a comercios."),
          ),
        );
      } else {
        // Cuando el operador no tiene config, el backend responde 200 con una
        // vacía: cualquier error acá es un fallo real, nunca "todavía no hay".
        const message = apiErrorMessage(err, t("No se pudo cargar la tarifa de cobro"));
        setLoadError(message);
        showToast(message, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cobertura del operador para acotar los selectores de comuna. `null` (y no `[]`)
  // mientras el documento de la empresa no llegó o falló: para el editor, "no se sabe" y
  // "no hay cobertura" son estados distintos.
  const coverageAreas = company ? (company.coverageAreas ?? []) : null;

  // Sucio = el BODY cambiaría, no el formulario. Ver `payloadFromForm`.
  const dirty = useMemo(
    () =>
      JSON.stringify(payloadFromForm(form, postalEnabled)) !==
      JSON.stringify(payloadFromForm(saved, postalEnabled)),
    [form, saved, postalEnabled],
  );

  // Una escala sin tramos no es utilizable: el backend rechaza dejarla como predeterminada
  // y el motor la degrada a plana. Se calcula sobre el PAYLOAD (filas en blanco fuera) para
  // que cargar una fila a medias no la dé por válida.
  const hasWeeklyTiers = useMemo(
    () => volumeTiersToApi(form.weeklyTiers).length > 0,
    [form.weeklyTiers],
  );
  const hasMonthlyTiers = useMemo(
    () => volumeTiersToApi(form.monthlyTiers).length > 0,
    [form.monthlyTiers],
  );
  // Se puede llegar acá vaciando la escala que ya era la predeterminada. Se bloquea el
  // guardado en vez de corregirlo solo: cambiarle la escala a toda la cartera en silencio
  // es justo lo que no debe pasar.
  const defaultScaleBroken =
    (form.defaultVolumeScale === "weekly" && !hasWeeklyTiers) ||
    (form.defaultVolumeScale === "monthly" && !hasMonthlyTiers);

  const setRow = (key, i, patch) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    }));
  const addRow = (key, row) =>
    setForm((f) => ({ ...f, [key]: [...f[key], row] }));
  const removeRow = (key, i) =>
    setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    const payload = payloadFromForm(form, postalEnabled);

    setSaving(true);
    try {
      const cfg = unwrap(await saveCommercePricingConfig(payload));
      const next = formFromConfig(cfg);
      setForm(next);
      setSaved(next);
      setConfigVersion(cfg?.configVersion ?? configVersion + 1);
      showToast(t("Tarifa de cobro guardada"), "success");
    } catch (err) {
      // El backend rechaza comunas o categorías duplicadas con un mensaje accionable
      // (dice cuál); mostrarlo tal cual es más útil que un genérico. Los arrays de
      // class-validator (400) se unen con ' · ' en vez de concatenarse sin separador.
      showToast(
        apiErrorMessage(err, t("No se pudo guardar la tarifa de cobro")),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SectionCard title={t("Cobro a comercios")}>
        <Skeleton className="h-64 w-full rounded-lg" />
      </SectionCard>
    );
  }

  if (forbidden) {
    return (
      <SectionCard title={t("Cobro a comercios")}>
        <div
          role="alert"
          className="rounded-lg border border-border bg-status-error-soft p-4 text-sm text-status-error"
        >
          {forbidden}
        </div>
      </SectionCard>
    );
  }

  if (loadError) {
    const title = t("No se pudo cargar la tarifa de cobro");
    return (
      <SectionCard title={t("Cobro a comercios")}>
        <div role="alert">
          <EmptyState
            icon={<AlertTriangle />}
            title={title}
            // El motivo real del backend va primero y en rojo; cuando no aporta
            // nada (un 5xx pelado cae al texto por defecto) no se repite.
            description={
              <>
                {loadError !== title && (
                  <>
                    <span className="text-status-error">{loadError}</span>{" "}
                  </>
                )}
                {t(
                  "Para no reemplazar la tarifa guardada con valores vacíos, el formulario no se muestra hasta poder cargarla.",
                )}
              </>
            }
            action={
              <Button
                variant="outline"
                iconLeft={<RefreshCw className="w-4 h-4" aria-hidden />}
                onClick={load}
              >
                {t("Reintentar")}
              </Button>
            }
          />
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      icon={<Coins className="w-4 h-4" />}
      title={t("Cobro a comercios")}
      subtitle={t(
        "Define cuánto le cobras a tus comercios por cada pedido. Esta tarifa aplica a todos; el contrato de un comercio puede tener su propia tarifa y esa gana.",
      )}
      action={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={saving}
            iconLeft={<RefreshCw className="w-4 h-4" />}
          >
            {t("Recargar")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={saving || !dirty || defaultScaleBroken}
            iconLeft={<Save className="w-4 h-4" />}
          >
            {saving ? t("Guardando…") : t("Guardar")}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {/* Tratamiento de IVA */}
        <div>
          <label className="text-sm font-medium text-fg">
            {t("Tratamiento de IVA")}
          </label>
          <p className="text-xs text-fg-muted mb-1">
            {t(
              "Define si los precios de abajo llevan IVA. La tasa se aplica según el país donde opera tu empresa (ej. Chile 19%).",
            )}
          </p>
          <select
            className={`${inputCls} max-w-[360px]`}
            value={form.taxMode}
            onChange={(e) => setForm((f) => ({ ...f, taxMode: e.target.value }))}
          >
            <option value="">{t("Sin IVA")}</option>
            <option value="exclusive">
              {t("Los precios son netos — se agrega el IVA del país")}
            </option>
            <option value="inclusive">{t("Los precios ya incluyen IVA")}</option>
          </select>
        </div>

        {/* Precio base */}
        <div>
          <label className="text-sm font-medium text-fg">
            {t("Precio base por pedido")} ({currency})
          </label>
          <p className="text-xs text-fg-muted mb-1">
            {t(
              "Se cobra en las comunas que no tengan una tarifa propia más abajo. Vacío = esas comunas quedan “sin tarifar” y no se cobran hasta que les pongas precio.",
            )}
          </p>
          <input
            type="number"
            min="0"
            className={`${inputCls} max-w-[200px]`}
            value={form.defaultBaseCost}
            onChange={(e) =>
              setForm((f) => ({ ...f, defaultBaseCost: e.target.value }))
            }
            placeholder={t("Ej: 3000")}
          />
        </div>

        {/* Tarifa escalonada por volumen. Va acá, pegada al precio base, porque es
            EXACTAMENTE lo que reemplaza: el tramo alcanzado pisa el precio base de todos
            los pedidos del ciclo. Las tarifas por comuna y por código postal se resuelven
            antes y le siguen ganando. */}
        <div className="rounded-lg border border-border/60 p-3 flex flex-col gap-4">
          <div>
            <label className="text-sm font-medium text-fg">
              {t("Tarifa escalonada por volumen")}
            </label>
            <p className="text-xs text-fg-muted mb-2">
              {t(
                "Precio por pedido que baja según cuántos pedidos haga el comercio en el ciclo. Al alcanzar un tramo, ese precio se aplica a TODOS los pedidos del ciclo, no solo a los que superan el umbral.",
              )}
            </p>
            <div className="flex items-start gap-2 text-xs text-fg-muted">
              <Info className="w-4 h-4 shrink-0 mt-px" />
              <span>
                {t(
                  "El tramo reemplaza el precio base de arriba. Los pedidos con tarifa propia por comuna o código postal conservan la suya: a un comercio con todas sus comunas tarifadas, el escalonado no le aplica.",
                )}
              </span>
            </div>
          </div>

          <VolumeTiersEditor
            title={t("Escala semanal (domingo a sábado)")}
            description={t(
              "Se cuenta de domingo a sábado. Un comercio con esta escala se factura semana por semana.",
            )}
            rows={form.weeklyTiers}
            onChange={(weeklyTiers) => setForm((f) => ({ ...f, weeklyTiers }))}
            currency={currency}
          />

          <VolumeTiersEditor
            title={t("Escala mensual (mes calendario)")}
            description={t(
              "Se cuenta del día 1 al último del mes. Un comercio con esta escala se factura mes por mes.",
            )}
            rows={form.monthlyTiers}
            onChange={(monthlyTiers) => setForm((f) => ({ ...f, monthlyTiers }))}
            currency={currency}
          />

          <div>
            <label className="text-sm font-medium text-fg">
              {t("Escala por defecto")}
            </label>
            <p className="text-xs text-fg-muted mb-1">
              {t(
                "Rige para los comercios que no tengan una escala propia en su contrato. Si la cambiás, todos esos comercios se mueven con ella.",
              )}
            </p>
            <select
              className={`${inputCls} max-w-[360px]`}
              value={form.defaultVolumeScale}
              onChange={(e) =>
                setForm((f) => ({ ...f, defaultVolumeScale: e.target.value }))
              }
            >
              <option value="flat">
                {t("Tarifa plana — sin escalonado")}
              </option>
              {/* Una escala sin tramos no se puede dejar como default: el backend lo
                  rechaza, así que se deshabilita en vez de dejar guardar y fallar. */}
              <option value="weekly" disabled={!hasWeeklyTiers}>
                {hasWeeklyTiers
                  ? t("Escala semanal")
                  : t("Escala semanal — cargá al menos un tramo")}
              </option>
              <option value="monthly" disabled={!hasMonthlyTiers}>
                {hasMonthlyTiers
                  ? t("Escala mensual")
                  : t("Escala mensual — cargá al menos un tramo")}
              </option>
            </select>
            {defaultScaleBroken && (
              <div className="flex items-start gap-2 text-xs text-status-error mt-2">
                <Info className="w-4 h-4 shrink-0 mt-px" />
                <span>
                  {t(
                    "La escala predeterminada se quedó sin tramos. Cargá al menos uno o volvé a tarifa plana para poder guardar.",
                  )}
                </span>
              </div>
            )}
            {form.defaultVolumeScale !== "flat" && !defaultScaleBroken && (
              <div className="flex items-start gap-2 text-xs text-fg-muted mt-2">
                <Info className="w-4 h-4 shrink-0 mt-px" />
                <span>
                  {t(
                    "Con una escala por defecto, sus notas de cobro solo se pueden emitir cubriendo el ciclo completo. El asistente de cobro va a filtrar los comercios según el período que elijas.",
                  )}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Tarifas por comuna. Los selectores se acotan a la cobertura declarada en la
            pestaña "Cobertura"; sin cobertura declarada se ofrece el catálogo completo
            del país (el editor lo explica en pantalla). */}
        <CommuneRatesEditor
          rows={form.zones}
          onChange={(zones) => setForm((f) => ({ ...f, zones }))}
          currency={currency}
          coverageAreas={coverageAreas}
          coverageLoading={loadingCompany}
        />

        {/* Tarifas por código postal (solo países que tarifan por CP) */}
        {postalEnabled && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-fg">
                {t("Tarifas por código postal")}
              </label>
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<Plus className="w-4 h-4" />}
                onClick={() =>
                  addRow("postalCodePricing", {
                    id: postalUid(),
                    codes: [],
                    baseCost: "",
                  })
                }
              >
                {t("Agregar")}
              </Button>
            </div>
            <p className="text-xs text-fg-muted mb-2">
              {t(
                "Le ganan a la tarifa por comuna. Se acepta el CPA de 8 caracteres (M5500ABC) o el código de 4 dígitos (5500); el CPA exacto le gana al de 4 dígitos.",
              )}
            </p>
            {form.postalCodePricing.length === 0 && (
              <p className="text-xs text-fg-muted">
                {t("Sin tarifas por código postal.")}
              </p>
            )}
            {form.postalCodePricing.map((p, i) => (
              <div
                key={p.id ?? i}
                className="flex gap-2 mb-2 items-end flex-wrap p-2 rounded-md border border-border/60"
              >
                {/* Chips validados en vez de texto libre: el mismo control que usa el
                    pago a repartidores. Un CP que no es tarifable (p. ej. uno chileno de
                    7 dígitos) se marca al escribirlo y no se envía, en vez de guardarse
                    como una regla muerta que el motor nunca va a buscar. */}
                <div className="flex-1 min-w-[220px]">
                  <PostalCodeChipsInput
                    label={t("Códigos postales")}
                    value={p.codes}
                    onChange={(codes) => setRow("postalCodePricing", i, { codes })}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-fg-muted">
                    {t("Precio")} ({currency})
                  </span>
                  <input
                    type="number"
                    min="0"
                    className={`${inputCls} max-w-[140px]`}
                    value={p.baseCost}
                    onChange={(e) =>
                      setRow("postalCodePricing", i, { baseCost: e.target.value })
                    }
                    placeholder={t("Ej: 4000")}
                    aria-label={t("Precio")}
                  />
                </div>
                <button
                  type="button"
                  className="h-9 px-2 text-fg-muted hover:text-status-error"
                  onClick={() => removeRow("postalCodePricing", i)}
                  aria-label={t("Quitar tarifa por código postal")}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Sobredimensionados */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-fg">
              {t("Recargo por sobredimensionado")}
            </label>
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<Plus className="w-4 h-4" />}
              onClick={() =>
                addRow("sizeSurcharges", {
                  category: "",
                  multiplier: "1",
                  additionalCost: "0",
                })
              }
            >
              {t("Agregar")}
            </Button>
          </div>
          <p className="text-xs text-fg-muted mb-2">
            {t(
              "Se cobra: precio de la comuna × multiplicador + monto fijo. Para cobrar solo un monto fijo, dejá el multiplicador en 1.",
            )}
          </p>
          <div className="flex items-start gap-2 mb-2 text-xs text-fg-muted">
            <Info className="w-4 h-4 shrink-0 mt-px" />
            <span>
              {t(
                "Una categoría sin recargo configurado se cobra al precio normal. El paquete se marca igual como sobredimensionado, pero no suma nada.",
              )}
            </span>
          </div>
          {form.sizeSurcharges.length === 0 && (
            <p className="text-xs text-fg-muted">
              {t("Sin recargos: los sobredimensionados se cobran al precio normal.")}
            </p>
          )}
          {form.sizeSurcharges.map((s, i) => (
            <div key={i} className="flex gap-2 mb-2 items-center flex-wrap">
              <select
                className={`${inputCls} max-w-[150px]`}
                value={s.category}
                onChange={(e) =>
                  setRow("sizeSurcharges", i, { category: e.target.value })
                }
              >
                <option value="">{t("Categoría…")}</option>
                {SIZE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <input
                type="number"
                step="0.1"
                min="0"
                className={`${inputCls} max-w-[120px]`}
                value={s.multiplier}
                onChange={(e) =>
                  setRow("sizeSurcharges", i, { multiplier: e.target.value })
                }
                placeholder={t("×Mult.")}
                title={t("Multiplicador sobre el precio de la comuna (2 = doble)")}
              />
              <input
                type="number"
                min="0"
                className={`${inputCls} max-w-[120px]`}
                value={s.additionalCost}
                onChange={(e) =>
                  setRow("sizeSurcharges", i, { additionalCost: e.target.value })
                }
                placeholder={t("+ Fijo")}
                title={t("Monto fijo adicional")}
              />
              <button
                type="button"
                className="h-9 px-2 text-fg-muted hover:text-status-error"
                onClick={() => removeRow("sizeSurcharges", i)}
                aria-label={t("Quitar")}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {configVersion > 0 && (
          <p className="text-xs text-fg-muted">
            {t("Versión de la tarifa")}:{" "}
            <span className="font-medium text-fg">v{configVersion}</span>
            {". "}
            {t(
              "Si la editás mientras alguien está generando notas de cobro, esa generación se detiene y pide reconfirmar con los precios nuevos.",
            )}
          </p>
        )}
      </div>
    </SectionCard>
  );
}
